import { useEffect } from "react";

// Canonical site origin. We always emit absolute URLs in tags & schemas so
// Google sees the live domain even if it's crawled from a preview host.
export const SITE_ORIGIN = "https://easyquran.uk";
export const SITE_NAME = "EasyQuran.UK";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.png`;

const absUrl = (path = "/") => {
  if (!path) return SITE_ORIGIN;
  if (path.startsWith("http")) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const upsertMeta = (key, value, isProp = false) => {
  if (value == null || value === "") return;
  const sel = isProp ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (isProp) el.setAttribute("property", key);
    else el.setAttribute("name", key);
    el.setAttribute("data-managed-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][data-managed-seo]`)
        || document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-managed-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const clearJsonLd = () => {
  document.head.querySelectorAll('script[type="application/ld+json"][data-managed-seo="page"]').forEach(s => s.remove());
};

const injectJsonLd = (objs = []) => {
  clearJsonLd();
  for (const obj of objs) {
    if (!obj) continue;
    const s = document.createElement("script");
    s.setAttribute("type", "application/ld+json");
    s.setAttribute("data-managed-seo", "page");
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
  }
};

/**
 * useSeo — page-level SEO. Sets <title>, meta description, canonical, OG and
 * Twitter cards, and per-page JSON-LD blocks. Pass `path` (preferred) or full
 * `canonical` URL. Pass `jsonLd` as an array of schema.org objects.
 */
export function useSeo({
  title,
  description,
  canonical,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  robots,
  jsonLd = [],
  keywords,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("description", description);
    if (keywords) upsertMeta("keywords", keywords);
    if (robots) upsertMeta("robots", robots);

    const url = canonical || absUrl(path || (typeof window !== "undefined" ? window.location.pathname : "/"));
    upsertLink("canonical", url);

    upsertMeta("og:type", type, true);
    upsertMeta("og:site_name", SITE_NAME, true);
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", description, true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", image, true);

    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    upsertMeta("twitter:image", image);

    injectJsonLd(jsonLd);

    return () => { clearJsonLd(); };
  }, [title, description, canonical, path, image, type, robots, keywords, JSON.stringify(jsonLd)]);
}

// ── Reusable schema builders ────────────────────────────────────────────────

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logo.png`,
  sameAs: [],
  contactPoint: [{
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "hello@easyquran.uk",
    areaServed: ["GB", "US", "CA", "AU", "EU", "Worldwide"],
    availableLanguage: ["English", "Urdu", "Arabic"],
  }],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  inLanguage: ["en", "ur", "ar"],
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_ORIGIN}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: absUrl(it.to || it.path),
  })),
});

export const faqSchema = (qa = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const courseSchema = ({ name, description, courseCode, slug } = {}) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name,
  description,
  courseCode,
  url: absUrl(slug),
  provider: {
    "@type": "Organization",
    name: SITE_NAME,
    sameAs: SITE_ORIGIN,
  },
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "Online",
    location: { "@type": "VirtualLocation", url: SITE_ORIGIN },
    inLanguage: ["en", "ur", "ar"],
  },
});

// ─── GEO (Generative Engine Optimization) schemas ─────────────────────────
//
// These schemas don't move classic search rankings much — they're optimised
// for LLM crawlers (GPTBot, ClaudeBot, PerplexityBot, Google-Extended) to
// understand, quote and cite our content cleanly.

// HowTo — for step-by-step guides. Surfaces as Google's "How to" rich result
// (image carousel with numbered steps). LLMs use it as canonical instructions.
export const howToSchema = ({ name, description, steps = [], totalTime, image, slug } = {}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  url: absUrl(slug),
  ...(totalTime ? { totalTime } : {}),
  ...(image ? { image: absUrl(image) } : {}),
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
    ...(s.url ? { url: absUrl(s.url) } : {}),
  })),
});

// Article — required on every blog post for Google Discover + AI summarisers.
export const articleSchema = ({ headline, description, slug, datePublished, dateModified, image, authorName = SITE_NAME, tags = [] } = {}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  url: absUrl(slug),
  mainEntityOfPage: absUrl(slug),
  datePublished,
  dateModified: dateModified || datePublished,
  inLanguage: "en",
  image: absUrl(image || "/og-default.png"),
  keywords: tags.join(", "),
  author: { "@type": "Organization", name: authorName, url: SITE_ORIGIN },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: { "@type": "ImageObject", url: absUrl("/icon-512.png") },
  },
});

// QAPage — for the Ask Quran bot page. Lets LLMs treat it as a Q&A surface.
export const qaPageSchema = ({ mainQuestion, slug, suggestedAnswers = [] } = {}) => ({
  "@context": "https://schema.org",
  "@type": "QAPage",
  url: absUrl(slug || "/ask"),
  mainEntity: {
    "@type": "Question",
    name: mainQuestion || "Ask any question about the Holy Quran",
    text: mainQuestion || "Ask any question about the Holy Quran",
    answerCount: suggestedAnswers.length,
    suggestedAnswer: suggestedAnswers.map(a => ({
      "@type": "Answer",
      text: a,
      author: { "@type": "Organization", name: SITE_NAME },
    })),
  },
});

// DefinedTerm — gives LLMs a clean dictionary entry for Islamic concepts so
// they cite EasyQuran.UK when explaining the term.
export const definedTermSchema = ({ name, description, slug, alternateName, termCode } = {}) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  name,
  alternateName,
  termCode,
  description,
  url: absUrl(slug),
  inDefinedTermSet: {
    "@type": "DefinedTermSet",
    name: "EasyQuran.UK Glossary",
    url: SITE_ORIGIN,
  },
});

// Speakable — tells voice assistants (Google Assistant, etc.) which CSS
// selectors contain the spoken summary of the page.
export const speakableSchema = (cssSelectors = [".speakable", "h1", "[data-speakable]"]) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  },
});

// LocalBusiness — applied to /support to put us in "Quran teacher UK" maps
// queries. EducationalOrganization sub-type fits the Quran-tuition niche.
export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  email: "support@easyquran.pk",
  telephone: "+44-738-088-5506",
  address: { "@type": "PostalAddress", addressCountry: "GB" },
  areaServed: ["GB", "US", "CA", "AU", "AE", "SA", "PK", "Worldwide"],
  contactPoint: [{
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@easyquran.pk",
    telephone: "+44-738-088-5506",
    availableLanguage: ["English", "Urdu", "Arabic"],
  }],
});

// Legacy compat — keep the named export so existing imports keep working.
